import { Form, Radio, TimePicker, Checkbox } from 'antd'
import { Button, SelectField } from '@forwardflow/ui'
import { getTimeAsDayjs, timeZones } from '@forwardflow/helpers/utils'

interface BusinessHoursFormProps {
   form: any
   businessHours: any
   description?: string
   scheduleType: 'weekdays' | 'custom' | 'allWeek' | 'allWeekExceptSunday'
   setScheduleType: (
      type: 'weekdays' | 'custom' | 'allWeek' | 'allWeekExceptSunday'
   ) => void
   handleTimezoneChange: (value: string) => void
   handleAllWeekExceptSundayTimeChange: (
      time: any,
      type: 'startTime' | 'endTime'
   ) => void
   handleAllWeekTimeChange: (time: any, type: 'startTime' | 'endTime') => void
   handleWeekdayTimeChange: (time: any, type: 'startTime' | 'endTime') => void
   handleCheckboxChange: (index: number, checked: boolean) => void
   handleTimeChange: (
      index: number,
      time: any,
      type: 'startTime' | 'endTime'
   ) => void
   handleOkClick: () => React.MouseEvent<HTMLButtonElement>
   handleCancel?: () => void
   isLoading: boolean
   title?: string
   saveButtonPlacement?: 'top' | 'bottom'
}

export const BusinessHoursForm: React.FC<BusinessHoursFormProps> = ({
   form,
   businessHours,
   scheduleType,
   description = 'Forward Flow will only send messages and call the leads during these working hours but will continue the conversations if your lead replies after working hours.',
   setScheduleType,
   handleTimezoneChange,
   handleAllWeekExceptSundayTimeChange,
   handleAllWeekTimeChange,
   handleWeekdayTimeChange,
   handleCheckboxChange,
   handleTimeChange,
   handleOkClick,
   handleCancel,
   isLoading,
   title = 'Set working hours',
   saveButtonPlacement = 'bottom',
}) => {
   return (
      <>
         <Form
            layout="vertical"
            form={form}
            initialValues={businessHours}
            className="text-left text-white"
            autoComplete="off"
         >
            <>
               <div className="border-b border-white/20 px-8 pt-[30px] pb-5 mb-5 ">
                  <p className="text-[22px] font-medium mb-4">{title}</p>
                  <div className="flex items-center justify-between gap-x-5">
                     <p className="text-[#818181] text-[12px] font-medium">
                        {description}
                     </p>
                     {saveButtonPlacement === 'top' && (
                        <>
                           <Button
                              type="button"
                              isDisabled={isLoading}
                              onClick={handleCancel}
                              className="!h-10 !rounded px-7 mb-5 !mt-0 max-w-40 bg-white/10 border-primary/10 hover:border-primary/10 hover:bg-white/20"
                           >
                              Cancel
                           </Button>
                           <Button
                              type="submit"
                              isLoading={isLoading}
                              onClick={handleOkClick}
                              className="!h-10 !rounded px-7 mb-5 !mt-0 max-w-40"
                           >
                              Save
                           </Button>
                        </>
                     )}
                  </div>
               </div>
               <Form
                  layout="vertical"
                  form={form}
                  initialValues={businessHours}
                  className="text-left text-white px-11"
                  autoComplete="off"
               >
                  <div className="flex flex-col gap-x-5 sm:flex-row">
                     <div className="flex-1">
                        <p className="mb-2 text-[12px]">Select Time zone</p>
                        <SelectField
                           name="timezone"
                           isRequired
                           dataTestId="timezone-select"
                           errorMessage="Please choose a time zone"
                           placeholder="Time zone"
                           options={timeZones}
                           onChange={handleTimezoneChange}
                        />
                     </div>
                  </div>
                  <p className="text-[12px] mb-5">Select Business and days</p>
                  <Radio.Group
                     value={scheduleType}
                     onChange={(e) => setScheduleType(e.target.value)}
                     className="mb-4"
                  >
                     <div className="mb-4">
                        <Radio value="allWeekExceptSunday" className="ff-radio">
                           <span className="block !text-white">
                              All week (except Sunday)
                           </span>
                        </Radio>
                     </div>
                     {scheduleType === 'allWeekExceptSunday' && (
                        <div className="flex items-center gap-x-2">
                           <TimePicker
                              format="hh:mm a"
                              value={getTimeAsDayjs(
                                 businessHours?.days[0].startTime
                              )}
                              className="ff-time-picker"
                              showNow={false}
                              allowClear={true}
                              onChange={(time) =>
                                 handleAllWeekExceptSundayTimeChange(
                                    time,
                                    'startTime'
                                 )
                              }
                           />
                           <div className="mx-4 text-white/40">to</div>
                           <TimePicker
                              format="hh:mm a"
                              value={getTimeAsDayjs(
                                 businessHours?.days[0].endTime
                              )}
                              className="ff-time-picker"
                              showNow={false}
                              allowClear={true}
                              onChange={(time) =>
                                 handleAllWeekExceptSundayTimeChange(
                                    time,
                                    'endTime'
                                 )
                              }
                           />
                        </div>
                     )}
                     <div className="my-4">
                        <Radio value="allWeek" className="ff-radio">
                           <span className="block !text-white">All week</span>
                        </Radio>
                     </div>
                     {scheduleType === 'allWeek' && (
                        <div className="flex items-center gap-x-2">
                           <TimePicker
                              format="hh:mm a"
                              value={getTimeAsDayjs(
                                 businessHours?.days[0].startTime
                              )}
                              className="ff-time-picker"
                              showNow={false}
                              allowClear={true}
                              onChange={(time) =>
                                 handleAllWeekTimeChange(time, 'startTime')
                              }
                           />
                           <div className="mx-4 text-white/40">to</div>
                           <TimePicker
                              format="hh:mm a"
                              value={getTimeAsDayjs(
                                 businessHours?.days[0].endTime
                              )}
                              className="ff-time-picker"
                              showNow={false}
                              allowClear={true}
                              onChange={(time) =>
                                 handleAllWeekTimeChange(time, 'endTime')
                              }
                           />
                        </div>
                     )}
                     <div className="my-4">
                        <Radio value="weekdays" className="ff-radio">
                           <span className="block !text-white">
                              Weekdays (mon to fri)
                           </span>
                        </Radio>
                     </div>
                     {scheduleType === 'weekdays' && (
                        <div className="flex items-center gap-x-2">
                           <TimePicker
                              format="hh:mm a"
                              value={getTimeAsDayjs(
                                 businessHours?.days[0].startTime
                              )}
                              className="ff-time-picker"
                              showNow={false}
                              allowClear={true}
                              onChange={(time) =>
                                 handleWeekdayTimeChange(time, 'startTime')
                              }
                           />
                           <div className="mx-4 text-white/40">to</div>
                           <TimePicker
                              format="hh:mm a"
                              value={getTimeAsDayjs(
                                 businessHours?.days[0].endTime
                              )}
                              className="ff-time-picker"
                              showNow={false}
                              allowClear={true}
                              onChange={(time) =>
                                 handleWeekdayTimeChange(time, 'endTime')
                              }
                           />
                        </div>
                     )}
                     <div className="my-4">
                        <Radio value="custom" className="ff-radio">
                           <span className="block !text-white">Custom</span>
                        </Radio>
                     </div>
                     {scheduleType === 'custom' && (
                        <div className="flex flex-col p-4 border border-white/20 rounded-xl gap-y-2">
                           {businessHours?.days.map(
                              (day: any, index: number) => (
                                 <div
                                    key={index}
                                    className="flex items-center justify-between"
                                 >
                                    <Checkbox
                                       checked={day.enabled}
                                       onChange={(e) =>
                                          handleCheckboxChange(
                                             index,
                                             e.target.checked
                                          )
                                       }
                                    >
                                       <p className="w-[75px]">{day.day}</p>
                                    </Checkbox>
                                    <TimePicker
                                       format="hh:mm a"
                                       value={getTimeAsDayjs(day.startTime)}
                                       className="mr-2 ff-time-picker"
                                       disabled={!day.enabled}
                                       showNow={false}
                                       allowClear={true}
                                       onChange={(time) =>
                                          handleTimeChange(
                                             index,
                                             time,
                                             'startTime'
                                          )
                                       }
                                    />
                                    <TimePicker
                                       format="hh:mm a"
                                       value={getTimeAsDayjs(day.endTime)}
                                       className="ff-time-picker"
                                       disabled={!day.enabled}
                                       showNow={false}
                                       allowClear={true}
                                       onChange={(time) =>
                                          handleTimeChange(
                                             index,
                                             time,
                                             'endTime'
                                          )
                                       }
                                    />
                                 </div>
                              )
                           )}
                        </div>
                     )}
                  </Radio.Group>
                  {saveButtonPlacement === 'bottom' && (
                     <div className="pb-10">
                        <Button
                           type="submit"
                           isLoading={isLoading}
                           onClick={handleOkClick}
                        >
                           Confirm Business Hours
                        </Button>
                     </div>
                  )}
               </Form>
            </>
         </Form>
      </>
   )
}
